import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  ActionWithMoreInfoModel,
  ActionWithMoreInfoType
} from '@bo-client/activation-management/shared/models/actionWithMoreInfo.model';
import { RequestModel } from '@bo-client/shared/models/request.model';
import { UserDto } from '@bo-client/shared/models/userDto';
import { combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, map, startWith, takeUntil } from 'rxjs/operators';
import { Observe } from '@bo-client/shared/observe.util';
import { CustomButtonType } from '@bo-client/shared/components/custom-button/custom-button.component';
import { DataShareService } from '@bo-client/shared/services/data-share.service';

@Component({
  selector: 'app-dynamic-action-button-row',
  templateUrl: './dynamic-action-button-row.component.html',
  styleUrls: ['./dynamic-action-button-row.component.scss']
})
export class DynamicActionButtonRowComponent implements OnInit, OnDestroy {
  maxButtons$: Observable<number>; // initialer Wert

  private onDestroy$ = new Subject<void>();
  private resize$ = new Subject<void>();

  @Input()
  actions: ActionWithMoreInfoModel[] = [];

  @Observe('actions')
  actions$: Observable<ActionWithMoreInfoModel[]>;

  @Input()
  requestEntity: RequestModel;

  @Observe('requestEntity')
  requestEntity$: Observable<RequestModel>;

  tenantConfig$ = inject(DataShareService).tenantConfig$;

  @Input()
  currentUser: UserDto;

  @Input()
  alignActions: 'start' | 'center' | 'end' | 'space-around' | 'space-between' | 'space-evenly' = 'start';

  @Observe('currentUser')
  currentUser$: Observable<UserDto>;

  visibleActions$: Observable<ActionWithMoreInfoModel[]>;

  @Output()
  onActionClickedEvent = new EventEmitter<any>();

  buttonWidths: number[] = [];
  containerWidth$: Observable<number>;
  moreButtonWidth = 166.56;

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    this.containerWidth$ = this.resize$.pipe(
      startWith(null),
      takeUntil(this.onDestroy$),
      debounceTime(50),
      map(() => {
        return this.elementRef.nativeElement.offsetWidth;
      })
    );

    this.visibleActions$ = combineLatest([
      this.currentUser$,
      this.actions$,
      this.requestEntity$,
      this.tenantConfig$
    ]).pipe(
      map(([currentUser, actions, requestEntity, tenantConfig]) => {
        return actions.filter((action) =>
          action.visibleCondition(requestEntity, requestEntity.state, currentUser, tenantConfig)
        );
      })
    );

    this.maxButtons$ = combineLatest([this.visibleActions$, this.containerWidth$]).pipe(
      map(([visibleActions, containerWidth]) => {
        let cumulativeWidth = 0;
        let count = 0;

        console.log('containerWidth: ', containerWidth);

        for (const width of this.buttonWidths) {
          if (cumulativeWidth + width > containerWidth) {
            break;
          }
          if (
            count > 0 &&
            count < this.buttonWidths.length - 1 &&
            cumulativeWidth + width + this.moreButtonWidth > containerWidth
          ) {
            break;
          }
          cumulativeWidth += width;
          count++;
        }

        return count;
      })
    );
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }

  @HostListener('window:resize')
  onResize() {
    this.resize$.next();
  }

  public disableButton(action: ActionWithMoreInfoModel) {
    if (action.disabled) {
      return action.disabled(this.currentUser);
    } else {
      return false;
    }
  }

  mapCustomButtonType(actionType: ActionWithMoreInfoType): CustomButtonType {
    switch (actionType) {
      case 'primary':
        return 'flat';
      case 'secondary':
        return 'stroked';
      case 'danger':
        return 'danger';
      default:
        return 'flat';
    }
  }

  onButtonClicked(action: ActionWithMoreInfoModel) {
    this.onActionClickedEvent.emit(action);
  }
}
